import { GetQuickJournalNow } from "../Clock/QuickJournalClock";
import { TRenderable } from "source/app/presentation/Shared/SharedTypes";
import { IQuickJournalEntry } from "../QuickJournalTypes";

export function IsPersistableQuickJournalEntry(
    entry: IQuickJournalEntry | TRenderable | null
): entry is IQuickJournalEntry {
    return (
        !!entry &&
        typeof entry === "object" &&
        "text" in entry &&
        "timeStamp" in entry &&
        typeof entry.text === "string" &&
        typeof entry.timeStamp === "string" &&
        (!("createdAt" in entry) || typeof entry.createdAt === "string")
    );
}

export function MergeRecentEntries(
    ...entrySets: Array<Array<IQuickJournalEntry | TRenderable>>
): IQuickJournalEntry[] {
    const entriesByKey = new Map<string, IQuickJournalEntry>();

    entrySets
        .reduce<Array<IQuickJournalEntry | TRenderable>>(
            (allEntries, entries) => [...allEntries, ...entries],
            []
        )
        .forEach((entry, index) => {
            if (!IsPersistableQuickJournalEntry(entry)) {
                return;
            }

            const normalizedEntry = NormalizeQuickJournalEntry(entry, index);
            entriesByKey.set(
                `${normalizedEntry.createdAt}_${normalizedEntry.text}`,
                normalizedEntry
            );
        });

    return Array.from(entriesByKey.values())
        .sort(
            (firstEntry, secondEntry) =>
                GetEntrySortTime(firstEntry) - GetEntrySortTime(secondEntry)
        )
        .slice(-22);
}

export function NormalizeRecentEntries(
    entries: Array<IQuickJournalEntry | TRenderable>
): IQuickJournalEntry[] {
    return MergeRecentEntries(entries);
}

export function GetAddedEntryCreatedAts(
    before: Array<IQuickJournalEntry | TRenderable>,
    after: IQuickJournalEntry[]
): string[] {
    const existingKeys = new Set(
        before
            .filter(IsPersistableQuickJournalEntry)
            .map(GetQuickJournalEntryKey)
    );

    return Array.from(
        new Set(
            after
                .filter(IsPersistableQuickJournalEntry)
                .filter(
                    (
                        entry
                    ): entry is IQuickJournalEntry & { createdAt: string } =>
                        !!entry.createdAt &&
                        !existingKeys.has(GetQuickJournalEntryKey(entry))
                )
                .map((entry) => entry.createdAt)
        )
    );
}

function NormalizeQuickJournalEntry(
    entry: IQuickJournalEntry,
    index: number
): IQuickJournalEntry {
    const createdAt =
        entry.createdAt && IsValidDate(entry.createdAt)
            ? entry.createdAt
            : GetLegacyCreatedAt(entry.timeStamp, index);

    return {
        text: entry.text,
        timeStamp: entry.timeStamp,
        createdAt,
    };
}

function GetLegacyCreatedAt(timeStamp: string, index: number): string {
    const hours = Number.parseInt(timeStamp.substring(0, 2), 10);
    const minutes = Number.parseInt(timeStamp.substring(2, 4), 10);
    const createdAt = GetQuickJournalNow();

    if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
        createdAt.setHours(hours, minutes, index, 0);
    } else {
        createdAt.setSeconds(createdAt.getSeconds() + index);
    }

    return createdAt.toISOString();
}

function GetEntrySortTime(entry: IQuickJournalEntry): number {
    const createdAt = entry.createdAt || "";
    const dateValue = new Date(createdAt).getTime();

    return Number.isNaN(dateValue) ? 0 : dateValue;
}

function GetQuickJournalEntryKey(entry: IQuickJournalEntry): string {
    return `${entry.createdAt}_${entry.text}`;
}

function IsValidDate(dateText: string): boolean {
    return !Number.isNaN(new Date(dateText).getTime());
}
